<template>
  <div>
    <van-search
      autocomplete="off"
      v-model="queryParams.orderNo"
      @search="onSearch"
      placeholder="请输入订单号进行查询"
    >
      <template #right-icon>
        <van-icon name="filter-o" @click="openFilter" />
      </template>
    </van-search>
    <van-list
      v-model="loading"
      :finished="finished"
      finished-text="已显示全部"
      @load="getList(true)"
    >
      <div
        v-for="item in tableData"
        :key="item.orderId"
        class="order-card"
        @click="goDetail(item)"
      >
        <!-- 标题行：订单渠道、订单号、支付状态 -->
        <div class="card-row top">
          <span class="channel">{{ getChannelText(item) }}</span>
          <span class="order-no">{{ item.orderNo || "-" }}</span>
          <van-tag :type="getStatusType(item.lifecycleStatus)">{{
            getStatusText(item)
          }}</van-tag>
        </div>
        <!-- 内容块：右侧箭头 -->
        <div class="content-body">
          <div class="content-left">
            <div class="card-row">
              <span class="label">下单时间</span>
              <span class="value">{{ item.createTime || "-" }}</span>
            </div>
            <div class="card-row">
              <span class="label">总金额</span>
              <span class="value ">￥{{ item.totalAmount ?? "-" }}</span>
            </div>
            <div class="card-row">
              <span class="label">实收金额</span>
              <span class="value">￥{{
                item.receivedAmount ?? item.payAmount ?? "-"
              }}</span>
            </div>
            <div class="card-row">
              <span class="label">创客</span>
              <span class="value">{{ item.userId || "-" }}</span>
              <span class="ml-2 value" v-if="item.phone"
                >(手机号：{{ item.phone || "-" }})</span
              >
            </div>
            <div class="card-row">
              <span class="label">支付方式</span>
              <span class="value">{{ getPayTypeText(item) }}</span>
            </div>
          </div>
          <van-icon class="arrow" name="arrow" />
        </div>
      </div>
    </van-list>

    <!-- 筛选弹窗 -->
    <van-popup
      v-model:show="filterVisible"
      position="top"
      :style="{ height: '45%' }"
    >
      <div class="filter-content">
        <div class="filter-title">筛选条件</div>
        <div class="filter-item">
          <div class="filter-label">下单时间</div>
          <QuickDatePicker
            :start-time.sync="filterForm.startTime"
            :end-time.sync="filterForm.endTime"
            defaultType="today"
          />
        </div>
        <div class="filter-item">
          <div class="filter-label">订单渠道</div>
          <div class="filter-options">
            <span
              v-for="option in channelOptions"
              :key="option.value"
              :class="{ active: filterForm.retailTypes.includes(option.value) }"
              @click="toggleChannel(option.value)"
            >
              {{ option.label }}
            </span>
          </div>
        </div>
        <div class="filter-item">
          <div class="filter-label">订单状态</div>
          <div class="filter-options">
            <span
              v-for="option in statusOptions"
              :key="option.value"
              :class="{ active: filterForm.lifecycleStatus === option.value }"
              @click="selectStatus(option.value)"
            >
              {{ option.label }}
            </span>
          </div>
        </div>
        <div class="filter-actions">
          <van-button size="small" @click="resetFilter">重置</van-button>
          <van-button size="small" type="primary" @click="confirmFilter">
            确定
          </van-button>
        </div>
      </div>
    </van-popup>
  </div>
</template>
<script>
import { queryList } from "@/service/api/retail/order";
import QuickDatePicker from "@/components/quickDatePicker";
import { debounce } from "lodash";

export default {
  name: "RetailOrder",
  components: { QuickDatePicker },
  data() {
    return {
      loading: false,
      finished: false,
      tableData: [],
      totalCount: 0,
      pageIndex: 1,
      pageSize: 20,
      totalPage: 1,
      queryParams: {
        orderNo: "",
      },
      filterVisible: false,
      filterForm: {
        startTime: "",
        endTime: "",
        retailTypes: [],
        lifecycleStatus: null,
      },
      // 订单渠道：3-静态码 4-收银台 5-自助机
      channelOptions: [
        { label: "静态码", value: 3 },
        { label: "收银台", value: 4 },
        { label: "自助机", value: 5 },
      ],
      // 订单状态
      statusOptions: [
        { label: "待支付", value: 100 },
        { label: "已完成", value: 500 },
        { label: "已取消", value: 600 },
      ],
    };
  },
  created() {
    // 输入防抖：停止输入 1000ms 后触发查询
    this.debouncedSearch = debounce(() => {
      this.onSubmit();
    }, 1000);
  },
  mounted() {
    this.onSubmit();
  },
  beforeDestroy() {
    // 组件销毁时取消防抖，避免卸载后回调写入状态
    this.debouncedSearch.cancel();
  },
  watch: {
    "queryParams.orderNo"() {
      this.debouncedSearch();
    },
  },
  methods: {
    // 回车/点击搜索按钮：取消防抖立即查询，避免重复请求
    onSearch() {
      this.debouncedSearch.cancel();
      this.onSubmit();
    },
    openFilter() {
      this.filterVisible = true;
    },
    // 订单渠道多选（非变异方式更新数组）
    toggleChannel(value) {
      const list = this.filterForm.retailTypes;
      this.filterForm.retailTypes = list.includes(value)
        ? list.filter((v) => v !== value)
        : [...list, value];
    },
    // 订单状态单选，再次点击取消选择
    selectStatus(value) {
      this.filterForm.lifecycleStatus =
        this.filterForm.lifecycleStatus === value ? null : value;
    },
    resetFilter() {
      this.filterForm.startTime = "";
      this.filterForm.endTime = "";
      this.filterForm.retailTypes = [];
      this.filterForm.lifecycleStatus = null;
    },
    confirmFilter() {
      this.filterVisible = false;
      this.onSubmit();
    },
    onSubmit() {
      this.pageIndex = 1;
      this.tableData = [];
      this.finished = false;
      this.getList();
    },
    getList(load) {
      if (load) {
        this.pageIndex++;
      }
      this.loading = true;
      const params = {
        orderNo: this.queryParams.orderNo || undefined,
        startTime: this.filterForm.startTime
          ? this.filterForm.startTime + " 00:00:00"
          : "",
        endTime: this.filterForm.endTime
          ? this.filterForm.endTime + " 23:59:59"
          : "",
        retailTypes: this.filterForm.retailTypes.length
          ? this.filterForm.retailTypes
          : undefined,
        lifecycleStatus:
          this.filterForm.lifecycleStatus === null
            ? undefined
            : this.filterForm.lifecycleStatus,
        pageIndex: this.pageIndex,
        pageSize: this.pageSize,
      };
      queryList(params)
        .then((res) => {
          this.tableData = load
            ? this.tableData.concat(res.data.datas || [])
            : res.data.datas || [];
          this.loading = false;
          this.totalCount = res.data.totalCount || 0;
          this.totalPage = res.data.totalPage || 1;
          if (this.pageIndex >= this.totalPage) {
            this.finished = true;
          }
        })
        .catch(() => {
          this.loading = false;
          this.finished = true;
        });
    },
    // 订单渠道文案：优先后端描述，兜底前端映射
    getChannelText(item) {
      if (item.retailTypeDesc) return item.retailTypeDesc;
      const opt = this.channelOptions.find((c) => c.value === item.retailType);
      return opt ? opt.label : "-";
    },
    // 订单状态文案：优先后端描述，兜底前端映射
    getStatusText(item) {
      if (item.lifecycleStatusDesc) return item.lifecycleStatusDesc;
      const opt = this.statusOptions.find(
        (s) => s.value === item.lifecycleStatus,
      );
      return opt ? opt.label : "-";
    },
    getStatusType(status) {
      if (status === 500) return "success";
      if (status === 600) return "danger";
      return "warning";
    },
    // 支付方式：payTypeInfos 多支付渠道拼接
    getPayTypeText(item) {
      if (item.payTypeInfos && item.payTypeInfos.length) {
        const text = item.payTypeInfos
          .map((p) => p.payChannelDesc)
          .filter(Boolean)
          .join("、");
        return text || "-";
      }
      return "-";
    },
    goDetail(item) {
      this.$router.push(`/retail/orderDetail/${item.orderId}`);
    },
  },
};
</script>
<style lang="less" scoped>
.order-card {
  position: relative;
  background: #fff;
  margin: 8px 10px;
  padding: 10px 12px;
  border-radius: 8px;

  .card-row {
    display: flex;
    align-items: center;
    font-size: 13px;
    color: #666;
    justify-content: space-between;
    & + .card-row {
      margin-top: 8px;
    }

    .label {
      color: #999;
      min-width: 5rem;
    }

    .value {
      color: #333;
    }

    &.top {
      justify-content: space-between;
      border-bottom: 1px solid #e5e5e5;
      padding-bottom: 6px;
      .channel {
        font-size: 12px;
        color: #1989fa;
      }

      .order-no {
        flex: 1;
        margin: 0 8px;
        font-size: 14px;
        font-weight: 500;
        color: #333;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }

    .amount {
      color: #ee0a24;
      font-weight: 600;
    }
  }

  .content-body {
    display: flex;
    align-items: center;
    margin-top: 8px;

    .content-left {
      flex: 1;
    }

    .arrow {
      color: #c8c9cc;
      font-size: 16px;
      margin-left: 8px;
      flex-shrink: 0;
    }
  }
}

.filter-content {
  padding: 15px;

  .filter-title {
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 15px;
  }

  .filter-item {
    margin-bottom: 15px;

    .filter-label {
      font-size: 14px;
      margin-bottom: 8px;
      color: #333;
    }

    .filter-options {
      display: flex;
      gap: 10px;
      flex-wrap: wrap;

      span {
        padding: 4px 12px;
        border: 1px solid #e5e5e5;
        border-radius: 4px;
        font-size: 14px;
        cursor: pointer;

        &.active {
          background-color: #1989fa;
          color: #fff;
          border-color: #1989fa;
        }
      }
    }
  }

  .filter-actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    margin-top: 20px;
  }
}
</style>
